import { observer } from 'mobx-react';
import React, { useEffect, useState, useRef } from 'react';
import { ClaimEditorStore } from '../../stores/claim-editor';
import { BaseButton } from '../base/button';
import { Spinner } from '../base/spinner';
import { BaseDialog } from '../base/dialog/dialog';
import { AdditionalInformationForm } from './forms/additional-information-form';
import { AdditionalProvidersForm } from './forms/additional-providers-form';
import { BillingProviderForm } from './forms/additional-providers-form/billing-provider-form';
import { FacilityForm } from './forms/facility-form';
import { PatientForm } from './forms/patient-form';
import { SubscriberPayerList } from './forms/subscriber-payer-form';
import { ServicesList } from './forms/services-form/services-list';
import { GuarantorForm } from './forms/guarantor-form';
import { SettingsForm } from './forms/settings-form';
import { Latin1ValidationProvider, useLatin1ValidationContext } from '../contexts/latin1-validation-context';
import * as S from './claim-editor.styles';
import * as ErrorStyles from '../../services/auth/error.styles';

interface IClaimEditorProps {
    claimEditorStore: ClaimEditorStore;
    claimId?: string;
    isTemplate?: boolean;
    onCancel?: () => void;
    onSuccess?: (claimId: string) => void;
    disabledSections?: string[];
    customSections?: React.ReactNode;
    headerActions?: React.ReactNode;
}

const ClaimEditorContent: React.FunctionComponent<IClaimEditorProps> = observer((props) => {
    const {
        claimEditorStore,
        claimId,
        isTemplate = false,
        onCancel,
        onSuccess,
        disabledSections = [],
        customSections,
        headerActions,
    } = props;

    const hasClaimIdParam = Boolean(claimId);
    const isCreateMode = claimEditorStore?.isCreateClaim ?? !hasClaimIdParam;
    const isEditMode = !isCreateMode;
    const pageTitle = isCreateMode ? 'Create Claim' : 'Edit Claim';
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const textRef = useRef<HTMLTextAreaElement>(null);
    const saveTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const { getErrors } = useLatin1ValidationContext();

    useEffect(() => {
        const loadData = async () => {
            if (hasClaimIdParam && claimId && claimEditorStore) {
                await claimEditorStore.loadClaimData(claimId, isTemplate);
            }
        };

        loadData();

        // Preload payers on mount
        if (claimEditorStore && !(claimEditorStore as any)['payersLoaded']) {
            (claimEditorStore as any).preloadPayers();
        }

        // Cleanup on unmount
        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
            setIsSubmitting(false);
            claimEditorStore?.resetStore();
        };
    }, [claimId, hasClaimIdParam, claimEditorStore, isTemplate]);

    const handleSubmit = async () => {
        if (!claimEditorStore) return;

        // Set submitting state immediately to disable button and show loading
        setIsSubmitting(true);

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            try {
                // Trigger validation in all child forms so banners/errors appear
                claimEditorStore.triggerValidation();
                
                // Get Latin-1 validation errors
                const latin1Errors = getErrors();
                
                // Merge store validation blockers with Latin-1 errors
                const reasons = claimEditorStore.getSubmitBlockers();
                const allBlockers = [...reasons, ...latin1Errors];
                
                if (!claimEditorStore.canSubmit || latin1Errors.length > 0) {
                    const message = allBlockers.length ? `Cannot submit:\n- ${allBlockers.join('\n- ')}` : 'Please complete all required fields before submitting.';
                    setErrorMessage(message);
                    setIsSubmitting(false);
                    return;
                }
                try {
                    const newId = await claimEditorStore.submitClaim();
                    if (onSuccess && newId) {
                        onSuccess(newId);
                    }
                } catch (error) {
                    const errorMsg = error instanceof Error ? error.message : 'An error occurred while submitting the claim.';
                    setErrorMessage(errorMsg);
                }
            } catch (error) {
                // Handle errors during validation or canSubmit check
                const errorMsg = error instanceof Error ? error.message : 'An error occurred during validation.';
                setErrorMessage(errorMsg);
            } finally {
                setIsSubmitting(false);
            }
        }, 750);
    };

    const handleCloseError = () => {
        setErrorMessage(null);
    };

    const handleContinue = () => {
        handleCloseError();
    };

    const renderActions = () => {
        return (
            <ErrorStyles.DialogActions>
                <ErrorStyles.DialogButton variant="primary" onClick={handleContinue}>
                    Close
                </ErrorStyles.DialogButton>
            </ErrorStyles.DialogActions>
        );
    };

    const selectAll = () => {
        if (textRef && textRef.current) {
            textRef.current.select();
        }
    };

    // Only show full page spinner during initial data loading, not during submission
    if (claimEditorStore?.isLoading && !isSubmitting) {
        return (
            <S.LoadingContainer>
                <Spinner />
            </S.LoadingContainer>
        );
    }

    return (
        <S.Container>
            <S.Header>
                <S.Title>{pageTitle}</S.Title>
                {headerActions}
            </S.Header>

            <S.Content>
                {/* Patient Information Section */}
                {!disabledSections.includes('patient') && (
                    <S.Section>
                        <S.SectionTitle>Patient Information</S.SectionTitle>
                        <PatientForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Guarantor Information Section */}
                {!disabledSections.includes('guarantor') && (
                    <S.Section>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <S.SectionTitle>Guarantor Information</S.SectionTitle>
                            <BaseButton
                                type="button"
                                variant="outlined"
                                onClick={(): void => {
                                    claimEditorStore.copyGuarantorFromPatient();
                                }}
                                disabled={!claimEditorStore?.patient}
                            >
                                Copy From Patient
                            </BaseButton>
                        </div>
                        <GuarantorForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Subscriber Information Section */}
                {!disabledSections.includes('subscriber') && (
                    <S.Section>
                        <S.SectionTitle>Subscriber Information</S.SectionTitle>
                        <SubscriberPayerList 
                            initialItems={(claimEditorStore.activePayload as any)?.subscribers}
                            claimEditorStore={claimEditorStore}
                        />
                    </S.Section>
                )}

                {/* Services Section */}
                <S.Section>
                    <S.SectionTitle>Services</S.SectionTitle>
                    <ServicesList claimEditorStore={claimEditorStore} />
                </S.Section>

                {/* Provider Information Section */}
                {!disabledSections.includes('provider') && (
                    <S.Section>
                        <S.SectionTitle>Billing Provider Information</S.SectionTitle>
                        <BillingProviderForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Facility Information Section */}
                {!disabledSections.includes('facility') && (
                    <S.Section>
                        <S.SectionTitle>Facility Information</S.SectionTitle>
                        <FacilityForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Additional Providers Section */}
                {!disabledSections.includes('additionalProviders') && (
                    <S.Section>
                        <S.SectionTitle>Additional Providers</S.SectionTitle>
                        <AdditionalProvidersForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Additional Information Section */}
                {!disabledSections.includes('additionalInformation') && (
                    <S.Section>
                        <S.SectionTitle>Additional Information</S.SectionTitle>
                        <AdditionalInformationForm 
                            claimEditorStore={claimEditorStore} 
                            isEditMode={isEditMode} 
                        />
                    </S.Section>
                )}

                {/* Settings Section */}
                {!disabledSections.includes('settings') && (
                    <S.Section>
                        <S.SectionTitle>Settings</S.SectionTitle>
                        <SettingsForm claimEditorStore={claimEditorStore} />
                    </S.Section>
                )}

                {/* Custom sections provided by the app */}
                {customSections}

                {/* Submit Button */}
                <S.Section>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <BaseButton onClick={handleSubmit} disabled={isSubmitting || claimEditorStore?.isLoading}>
                            {isSubmitting || claimEditorStore?.isLoading
                                ? <Spinner size="20px" />
                                : claimEditorStore?.isCreateClaim
                                    ? 'Create Claim'
                                    : 'Edit Existing Claim'}
                        </BaseButton>
                        {onCancel && (
                            <BaseButton onClick={onCancel} disabled={isSubmitting || claimEditorStore?.isLoading}>
                                Cancel
                            </BaseButton>
                        )}
                    </div>
                </S.Section>
            </S.Content>
            {errorMessage && (
                <BaseDialog
                    open={true}
                    disableBackdropClick={true}
                    disableEscapeKeyDown={true}
                    renderTitle="Cannot submit claim"
                    renderActions={renderActions()}>
                    <ErrorStyles.DialogBody>
                        <ErrorStyles.Col>
                            {errorMessage}
                            <ErrorStyles.Textarea
                                ref={textRef}
                                rows={11}
                                value={errorMessage}
                                onClick={selectAll}
                                readOnly={true}
                            />
                        </ErrorStyles.Col>
                    </ErrorStyles.DialogBody>
                </BaseDialog>
            )}
        </S.Container>
    );
});

export const ClaimEditor: React.FunctionComponent<IClaimEditorProps> = (props) => {
    return (
        <Latin1ValidationProvider>
            <ClaimEditorContent {...props} />
        </Latin1ValidationProvider>
    );
};

